/**
 * Vault Store - Manages vault state for KnowledgeBase
 *
 * Handles:
 * - Loading and caching vaults
 * - Current active vault selection
 * - Vault CRUD operations
 */

import { acceptHMRUpdate, defineStore } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import VaultService from '../services/VaultService.js'
import NoteService from '../services/NoteService.js'
import { createCacheInfo, isCacheValid } from '@/stores/cacheUtils'
import { parseMarkdown, slugify } from '../services/LinkParser.js'

const normalizeVaultName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
const humanizeEntityId = (value) =>
  String(value || '')
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
import { normalizeDescriptionToMarkdownText } from '../services/htmlToMarkdown.js'
import { getProviderRegistry } from '../services/ProviderRegistry.js'
import { getEntityFolder } from '../config/entityFolders.js'
import { getFolderName, isChildOf, joinFolderPath } from '../services/FolderService.js'

const DEFAULT_CACHE_MINUTES = 5
const STORAGE_PREFIX = 'kb'
const STRIP_WIKILINKS_RE = /\[\[[^\]]+\]\]/g

const normalizeTerm = (value) => {
  if (!value) return ''
  // Normalize to a stable, punctuation-insensitive, unicode-friendly comparison form.
  // This enables mention matching for titles like "Acid (vial)" against plain text like "Acid (vial)".
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const isTokenCandidate = (token) => {
  if (!token) return false
  const t = String(token)
  if (t.length >= 3) return true
  // Allow short numeric / mixed tokens to anchor phrase matching at scale (e.g. "Note 1", "5e").
  if (/^\d+$/.test(t)) return true
  if (t.length >= 2 && /\d/.test(t)) return true
  return false
}

const buildKeywordCandidates = (note) => {
  if (!note || !note.id) return []
  const seen = new Map()
  const addCandidate = (value, label) => {
    if (!value) return
    const normalized = normalizeTerm(value)
    if (!normalized || normalized.length < 2) return
    if (!seen.has(normalized)) {
      seen.set(normalized, label || value)
    }
  }

  addCandidate(note.title, note.title)
  addCandidate(note.slug, note.title)
  if (Array.isArray(note.aliases)) {
    note.aliases.forEach((alias) => addCandidate(alias, alias))
  }
  const fm = note.frontmatter || {}
  const ent = fm.entity || {}
  const fmAliases = fm.aliases || fm.alias
  if (Array.isArray(fmAliases)) {
    fmAliases.forEach((alias) => addCandidate(alias, alias))
  } else if (typeof fmAliases === 'string') {
    fmAliases
      .split(/[;,]/)
      .map((value) => value.trim())
      .filter(Boolean)
      .forEach((alias) => addCandidate(alias, alias))
  }
  if (ent.label) addCandidate(ent.label, ent.label)
  if (fm.entityLabel) addCandidate(fm.entityLabel, fm.entityLabel)
  if (ent.id) addCandidate(ent.id, ent.id)
  if (ent.type && ent.id) addCandidate(`${ent.type}:${ent.id}`, `${ent.type}:${ent.id}`)
  return Array.from(seen.entries()).map(([normalized, display]) => ({
    normalized,
    display
  }))
}

const stripWikilinks = (text) => {
  if (typeof text !== 'string') return ''
  return text.replace(STRIP_WIKILINKS_RE, ' ')
}

export const useVaultStore = defineStore('vaultStore', {
  state: () => ({
    // List of user's vaults
    vaults: [],
    vaultsLoaded: false,
    vaultsCacheInfo: null,

    // Current active vault
    activeVaultId: null,
    activeVault: null,

    // All-vaults mode (show notes from all vaults)
    allVaultsMode: false,
    allVaultsNotes: [], // Notes from all vaults when in all-vaults mode
    allVaultsNotesLoaded: false,
    allVaultsNotesLoading: false,

    // UI-selected action vault (for targeting creates while in All Vaults mode)
    uiActionVaultId: null,

    // Settings
    suggestFromAllVaults: true, // When true, wikilink autocomplete suggests from all vaults

    // Notes for the active vault
    notes: [],
    notesLoaded: false,
    notesCacheInfo: null,
    activeNoteId: null,

    // Version counter - increments on any note change to trigger graph rebuilds
    notesVersion: 0,

    // Indexes for quick lookups
    notesById: {},
    notesBySlug: {},

    // Backlinks and mentions (computed from notes)
    backlinks: {}, // noteId -> [{ id, title, content }]
    mentions: {}, // noteId -> [{ id, title, content }]

    // Editing tracking
    editing: {},
    lastAccessed: {},
    usageCounts: {},

    // Wikilink creation state (used to delay graph rebuilds)
    linkingNotes: false,

    // UI coordination: request NotesList to jump/scroll to a folder/vault
    folderJump: null, // { vaultId, path, ts }

    // User context (set externally)
    _userContext: null,

    // Lock to prevent concurrent ensureDefaultVault calls
    _ensureDefaultVaultPromise: null,
    // Lock to dedupe concurrent loadVaults calls
    _loadVaultsPromise: null,
    // Lock to dedupe concurrent loadAllVaultsNotes calls
    _loadAllVaultsNotesPromise: null,

    // Loading states
    loading: false,
    error: null,
    vaultsLoadFailed: false,
    vaultsLoadErrorCode: null
  }),

  getters: {
    /**
     * Get current user context
     */
    userContext() {
      // Return explicit context if set, otherwise derive from userStore
      if (this._userContext) {
        return this._userContext
      }
      const userStore = useUserStore()
      return {
        userId: userStore.user?.uid || null,
        isAuthenticated: !!userStore.user
      }
    },

    /**
     * Get the active note object
     */
    activeNote() {
      if (!this.activeNoteId) return null
      return this.notesById[this.activeNoteId] || null
    },

    /**
     * Get notes sorted by title
     */
    sortedNotes() {
      return [...this.notes].sort((a, b) => (a.title || '').localeCompare(b.title || ''))
    },

    /**
     * Get effective notes (single vault or all vaults depending on mode)
     */
    effectiveNotes() {
      if (this.allVaultsMode) {
        return this.allVaultsNotes
      }
      return this.notes
    },

    /**
     * Get vault name by ID
     */
    getVaultName() {
      return (vaultId) => {
        const vault = this.vaults.find((v) => v.id === vaultId)
        return vault?.name || 'Unknown Vault'
      }
    },

    /**
     * Get folder metadata from active vault
     */
    folderMeta() {
      return this.activeVault?.folderMeta || {}
    },

    /**
     * Get vault settings
     */
    settings() {
      return this.activeVault?.settings || {}
    },

    /**
     * Compatibility getter for WikilinkAutocomplete
     * Returns a scope-like object with list, bySlug, usageCounts, lastAccessed
     * Uses all-vaults notes when suggestFromAllVaults is enabled or in allVaultsMode
     */
    activeScope() {
      const useAllVaults = this.suggestFromAllVaults || this.allVaultsMode
      if (useAllVaults && this.allVaultsNotesLoaded) {
        return {
          list: this.allVaultsNotes || [],
          byId: this.notesById || {},
          bySlug: this.notesBySlug || {},
          usageCounts: this.usageCounts || {},
          lastAccessed: this.lastAccessed || {},
          crossVault: true
        }
      }
      return {
        list: this.notes || [],
        byId: this.notesById || {},
        bySlug: this.notesBySlug || {},
        usageCounts: this.usageCounts || {},
        lastAccessed: this.lastAccessed || {},
        crossVault: false
      }
    }
  },

  actions: {
    _getVaultById(id) {
      if (!id) return null
      if (this.activeVault && this.activeVault.id === id) return this.activeVault
      return (this.vaults || []).find((v) => v && v.id === id) || null
    },

    _getDefaultNotePermission(ctx = null, targetVaultId = null) {
      try {
        const context = ctx || this.userContext || {}
        const vid = targetVaultId || this.activeVaultId || null
        const v = vid ? this._getVaultById(vid) : this.activeVault || null
        const settings = (v && v.settings) || {}
        const pref = String(settings.defaultNotePermission || '').toUpperCase()
        const access = String(v?.access || '').toUpperCase()
        if (pref === 'PRIVATE' || pref === 'PUBLIC' || pref === 'CAMPAIGN') return pref
        if (pref === 'INHERIT') {
          if (access === 'CAMPAIGN' || access === 'CAMPAIGN_EDIT') return 'CAMPAIGN'
          if (access === 'PUBLIC') return 'PUBLIC'
          return 'PRIVATE'
        }
        // Fallback to prior behavior
        return context.campaignId ? 'CAMPAIGN' : 'PRIVATE'
      } catch {
        return 'PRIVATE'
      }
    },
    // ========================================================================
    // CONTEXT
    // ========================================================================

    /**
     * Set user context for vault operations
     */
    setUserContext(context) {
      this._userContext = {
        userId: context?.userId || null,
        versionId: context?.versionId || null,
        campaignId: context?.campaignId || null,
        isAuthenticated: !!context?.userId
      }
    },

    _storageKey(suffix) {
      const ctx = this.userContext || {}
      const userId = ctx.userId || 'anon'
      const versionId = ctx.versionId || 'default'
      const campaignId = ctx.campaignId || 'personal'
      return `${STORAGE_PREFIX}:${suffix}:${userId}:${versionId}:${campaignId}`
    },

    loadAllVaultsModeSetting() {
      try {
        const key = this._storageKey('all-vaults-mode')
        const saved = localStorage.getItem(key)
        if (saved !== null) {
          this.allVaultsMode = saved === 'true'
        }
      } catch {
        // Ignore storage errors
      }
    },

    persistAllVaultsModeSetting(enabled) {
      try {
        const key = this._storageKey('all-vaults-mode')
        localStorage.setItem(key, String(!!enabled))
      } catch {
        // Ignore storage errors
      }
    },

    getPersistedActiveVaultId() {
      try {
        const key = this._storageKey('active-vault-id')
        return localStorage.getItem(key) || ''
      } catch {
        return ''
      }
    },

    persistActiveVaultId(vaultId) {
      try {
        const key = this._storageKey('active-vault-id')
        if (vaultId) localStorage.setItem(key, String(vaultId))
      } catch {
        // Ignore storage errors
      }
    },

    // ========================================================================
    // VAULT OPERATIONS
    // ========================================================================

    /**
     * Load all vaults for the current user
     */
    async loadVaults(ctx = {}) {
      const context = { ...this.userContext, ...ctx }
      if (!context.userId) return []

      // Check cache first
      if (this.vaultsLoaded && isCacheValid(this.vaultsCacheInfo, DEFAULT_CACHE_MINUTES)) {
        return this.vaults
      }

      // Dedupe concurrent calls
      if (this._loadVaultsPromise) {
        return await this._loadVaultsPromise
      }

      this._loadVaultsPromise = (async () => {
        const userStore = useUserStore()
        if (context.isAuthenticated && userStore) {
          const ready = await userStore.waitForAuthState({
            requireUser: true,
            timeoutMs: 5000
          })
          if (!ready) {
            throw new Error('Authenticated session is not ready yet')
          }
        }

        this.loading = true
        this.error = null
        this.vaultsLoadFailed = false
        this.vaultsLoadErrorCode = null

        try {
          const vaults = await VaultService.listVaults(context)
          this.vaults = vaults
          this.vaultsLoaded = true
          this.vaultsCacheInfo = createCacheInfo()
          return vaults
        } catch (e) {
          this.error = e.message
          this.vaultsLoadFailed = true
          this.vaultsLoadErrorCode = e?.code || null
          // If this was a permission race right after auth, retry once after brief delay
          try {
            const code = String(e?.code || e?.name || '').toLowerCase()
            const msg = String(e?.message || '').toLowerCase()
            const userStore = useUserStore()
            if (
              context.isAuthenticated &&
              (!userStore ||
                userStore.authReady === false ||
                code.includes('permission') ||
                msg.includes('permission'))
            ) {
              await new Promise((r) => setTimeout(r, 250))
              const retry = await VaultService.listVaults(context).catch(() => null)
              if (Array.isArray(retry)) {
                this.vaults = retry
                this.vaultsLoaded = true
                this.vaultsCacheInfo = createCacheInfo()
                return retry
              }
            }
          } catch {
            /* ignore retry errors */
          }
          console.error('[VaultStore] Failed to load vaults:', e)
          return []
        } finally {
          this.loading = false
        }
      })()

      try {
        return await this._loadVaultsPromise
      } finally {
        this._loadVaultsPromise = null
      }
    },

    /**
     * Create a new vault
     */
    async createVault(data, ctx = {}) {
      const context = { ...this.userContext, ...ctx }
      if (!context.userId) throw new Error('Must be logged in to create vault')

      if (!this.vaultsLoaded) {
        await this.loadVaults(context)
      }

      const newName = String(data?.name || '').trim()
      if (newName) {
        const nameKey = normalizeVaultName(newName)
        const campaignId = data?.campaignId ?? null
        const duplicate = this.vaults.find(
          (vault) =>
            normalizeVaultName(vault?.name) === nameKey &&
            (vault?.campaignId ?? null) === campaignId
        )
        if (duplicate) {
          throw new Error('A vault with this name already exists for this campaign.')
        }
      }

      const vault = await VaultService.createVault(data, context)
      this.vaults.push(vault)
      return vault
    },

    /**
     * Update a vault
     */
    async updateVault(vaultId, updates) {
      if (!this.vaultsLoaded) {
        await this.loadVaults()
      }
      // Store-level validations and normalization for access controls
      try {
        const current = this.vaults.find((v) => v.id === vaultId) || {}
        const nextAccess = (updates?.access || current?.access || 'PRIVATE')
          .toString()
          .toUpperCase()
        if (updates) updates.access = nextAccess

        if (nextAccess === 'CAMPAIGN' || nextAccess === 'CAMPAIGN_EDIT') {
          const raw =
            updates?.campaignIds ??
            current?.campaignIds ??
            (current?.campaignId ? [current.campaignId] : [])
          const list = Array.isArray(raw) ? Array.from(new Set(raw.filter(Boolean))) : []
          if (!list.length) throw new Error('Select at least one campaign')
          updates.campaignIds = list
          if (!updates.campaignId) updates.campaignId = list[0]
        } else if (nextAccess === 'USER_SPECIFIC') {
          const raw = updates?.allowedUserIds ?? current?.allowedUserIds ?? []
          const list = Array.isArray(raw) ? Array.from(new Set(raw.filter(Boolean))) : []
          if (!list.length) throw new Error('Pick at least one user')
          updates.allowedUserIds = list
          // Clear campaign link for user-specific to avoid confusion
          if (!updates.campaignIds) updates.campaignIds = []
          if (!updates.campaignId) updates.campaignId = null
        }
      } catch (e) {
        this.error = e?.message || 'Invalid vault access metadata'
        throw e
      }
      if (updates?.name) {
        const nameKey = normalizeVaultName(updates.name)
        const current = this.vaults.find((v) => v.id === vaultId)
        const campaignId = current?.campaignId ?? null
        const duplicate = this.vaults.find(
          (vault) =>
            vault.id !== vaultId &&
            normalizeVaultName(vault?.name) === nameKey &&
            (vault?.campaignId ?? null) === campaignId
        )
        if (duplicate) {
          throw new Error('A vault with this name already exists for this campaign.')
        }
      }
      const vault = await VaultService.updateVault(vaultId, updates)

      // Update in list
      const idx = this.vaults.findIndex((v) => v.id === vaultId)
      if (idx !== -1) {
        this.vaults[idx] = { ...this.vaults[idx], ...updates }
      }

      // Update active vault if it's the one being updated
      if (this.activeVaultId === vaultId && this.activeVault) {
        this.activeVault = { ...this.activeVault, ...updates }
      }

      return vault
    },

    /**
     * Delete a vault
     */
    async deleteVault(vaultId) {
      await VaultService.deleteVault(vaultId)

      this.vaults = this.vaults.filter((v) => v.id !== vaultId)

      if (this.activeVaultId === vaultId) {
        this.activeVaultId = null
        this.activeVault = null
        this.notes = []
        this.notesLoaded = false
      }
    },

    /**
     * Set the active vault and load its notes
     */
    async setActiveVault(vaultId, options = {}) {
      if (this.activeVaultId === vaultId && this.notesLoaded && !options.force) {
        return this.activeVault
      }

      // Reset notes state when switching vaults
      const isVaultSwitch = this.activeVaultId !== vaultId
      if (isVaultSwitch) {
        this.notes = []
        this.notesLoaded = false
        this.notesCacheInfo = null
        this.activeNoteId = null
        this._buildIndexes()
      }

      this.loading = true
      this.error = null

      try {
        // Load/refresh vault document to ensure folderMeta/settings are up to date
        let vault = await VaultService.getVault(vaultId)
        if (vault) {
          const idx = this.vaults.findIndex((v) => v.id === vaultId)
          if (idx >= 0) this.vaults.splice(idx, 1, vault)
          else this.vaults.push(vault)
        }

        if (!vault) {
          throw new Error('Vault not found')
        }

        this.activeVaultId = vaultId
        this.activeVault = vault
        this.persistActiveVaultId(vaultId)

        // Load notes for this vault. In public/shared views, prefer PUBLIC-only
        if (options.publicOnly) {
          await this.loadNotesPublic()
        } else {
          await this.loadNotes()
        }

        return vault
      } catch (e) {
        this.error = e.message
        console.error('[VaultStore] Failed to set active vault:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Get or create default vault
     * Uses a lock to prevent race conditions when called concurrently
     */
    async ensureDefaultVault(ctx = {}) {
      // If there's already an in-flight request, wait for it
      if (this._ensureDefaultVaultPromise) {
        return this._ensureDefaultVaultPromise
      }

      // Create the promise and store it
      this._ensureDefaultVaultPromise = this._doEnsureDefaultVault(ctx)

      try {
        return await this._ensureDefaultVaultPromise
      } finally {
        this._ensureDefaultVaultPromise = null
      }
    },

    /**
     * Internal implementation of ensureDefaultVault
     */
    async _doEnsureDefaultVault(ctx = {}) {
      const context = { ...this.userContext, ...ctx }
      if (!context.userId) return null

      // Load settings from localStorage
      this.loadSuggestFromAllVaultsSetting()
      this.loadAllVaultsModeSetting()

      await this.loadVaults(context)

      if (this.vaultsLoadFailed) {
        return null
      }

      if (this.vaults.length === 0) {
        // Create default vault
        const vault = await this.createVault(
          {
            name: context.campaignId ? 'Campaign Notes' : 'My Notes',
            campaignId: context.campaignId || null
          },
          context
        )
        return vault
      }

      // Return matching campaign vault or first vault
      if (context.campaignId) {
        const campaignVault = this.vaults.find((v) => v.campaignId === context.campaignId)
        if (campaignVault) return campaignVault
      }

      return this.vaults[0]
    },

    // ========================================================================
    // NOTE OPERATIONS
    // ========================================================================

    /**
     * Load notes for the active vault
     */
    async loadNotes() {
      if (!this.activeVaultId) return []

      // Check cache
      if (this.notesLoaded && isCacheValid(this.notesCacheInfo, DEFAULT_CACHE_MINUTES)) {
        return this.notes
      }

      this.loading = true

      try {
        const notes = await NoteService.listNotes(this.activeVaultId)
        this.notes = notes
        this.notesLoaded = true
        this.notesCacheInfo = createCacheInfo()
        this._buildIndexes()

        // Also load all-vaults notes if cross-vault suggestions are enabled
        if (this.suggestFromAllVaults && !this.allVaultsNotesLoaded) {
          // Load in background, don't block
          this.loadAllVaultsNotes().catch(() => {})
        }

        return notes
      } catch (e) {
        this.error = e.message
        console.error('[VaultStore] Failed to load notes:', e)
        return []
      } finally {
        this.loading = false
      }
    },

    /**
     * Load only PUBLIC notes for the active vault (for public/shared views)
     */
    async loadNotesPublic() {
      if (!this.activeVaultId) return []
      this.loading = true
      try {
        const notes = await NoteService.listPublicNotes(this.activeVaultId)
        this.notes = notes
        this.notesLoaded = true
        this.notesCacheInfo = createCacheInfo()
        this._buildIndexes()
        return notes
      } catch (e) {
        this.error = e.message
        console.error('[VaultStore] Failed to load public notes:', e)
        return []
      } finally {
        this.loading = false
      }
    },

    /**
     * Toggle all-vaults mode
     */
    async setAllVaultsMode(enabled) {
      this.allVaultsMode = enabled
      this.persistAllVaultsModeSetting(enabled)
      if (enabled) {
        await this.loadAllVaultsNotes()
      } else {
        // Leaving All Vaults: if current activeNote belongs to another vault, clear it
        try {
          const currentId = this.activeNoteId
          if (currentId) {
            const isInActiveVault = !!this.notesById[currentId]
            let noteVaultId = null
            if (isInActiveVault) {
              noteVaultId = this.activeVaultId
            } else if (Array.isArray(this.allVaultsNotes) && this.allVaultsNotes.length) {
              const found = this.allVaultsNotes.find((n) => n && n.id === currentId)
              noteVaultId = found?.vaultId || null
            }
            if (noteVaultId && this.activeVaultId && noteVaultId !== this.activeVaultId) {
              this.activeNoteId = null
            }
          }
        } catch {}
      }
      this.notesVersion++
    },

    /**
     * Update the UI action vault id used for create operations in All Vaults mode
     */
    setUiActionVault(vaultId) {
      this.uiActionVaultId = vaultId || null
      try {
        sessionStorage.setItem('kb-ui-action-vault', this.uiActionVaultId || '')
      } catch {}
    },
    loadUiActionVault() {
      try {
        const v = sessionStorage.getItem('kb-ui-action-vault') || ''
        this.uiActionVaultId = v || null
      } catch {}
    },

    /**
     * Set whether wikilink suggestions should include notes from all vaults
     */
    async setSuggestFromAllVaults(enabled) {
      this.suggestFromAllVaults = enabled
      // Persist to localStorage
      try {
        localStorage.setItem('kb-suggest-from-all-vaults', String(enabled))
      } catch {
        // Ignore storage errors
      }
      if (enabled && !this.allVaultsNotesLoaded) {
        await this.loadAllVaultsNotes()
      }
    },

    /**
     * Load suggest from all vaults setting from localStorage
     */
    loadSuggestFromAllVaultsSetting() {
      try {
        const saved = localStorage.getItem('kb-suggest-from-all-vaults')
        if (saved !== null) {
          this.suggestFromAllVaults = saved === 'true'
        }
      } catch {
        // Ignore storage errors
      }
    },

    /**
     * Load notes from all vaults
     */
    async loadAllVaultsNotes() {
      if (this.allVaultsNotesLoaded) {
        return this.allVaultsNotes
      }

      if (this._loadAllVaultsNotesPromise) {
        return await this._loadAllVaultsNotesPromise
      }

      this._loadAllVaultsNotesPromise = (async () => {
        this.allVaultsNotesLoading = true

        try {
          // Ensure vaults are loaded
          await this.loadVaults()

          // Load notes from each vault in parallel
          const allNotes = []
          const isAnon = !(this.userContext && this.userContext.isAuthenticated)
          const loadPromises = this.vaults.map(async (vault) => {
            try {
              const notes = isAnon
                ? await NoteService.listPublicNotes(vault.id)
                : await NoteService.listNotes(vault.id)
              // Add vaultId and vaultName to each note for identification
              return notes.map((note) => ({
                ...note,
                vaultId: vault.id,
                vaultName: vault.name
              }))
            } catch (e) {
              console.warn(`[VaultStore] Failed to load notes from vault ${vault.id}:`, e)
              return []
            }
          })

          const results = await Promise.all(loadPromises)
          for (const notes of results) {
            allNotes.push(...notes)
          }

          this.allVaultsNotes = allNotes
          this.allVaultsNotesLoaded = true

          // Rebuild indexes to include all notes
          this._buildAllVaultsIndexes()

          return allNotes
        } catch (e) {
          this.error = e.message
          console.error('[VaultStore] Failed to load all vault notes:', e)
          return []
        } finally {
          this.allVaultsNotesLoading = false
        }
      })()

      try {
        return await this._loadAllVaultsNotesPromise
      } finally {
        this._loadAllVaultsNotesPromise = null
      }
    },

    /**
     * Build indexes for all-vaults mode or cross-vault suggestions
     */
    _buildAllVaultsIndexes() {
      // Build indexes from allVaultsNotes for cross-vault features
      if (!this.allVaultsMode && !this.suggestFromAllVaults) return
      if (!this.allVaultsNotes?.length) return

      // When in allVaultsMode, replace indexes entirely
      // When just suggestFromAllVaults, merge into existing indexes
      if (this.allVaultsMode) {
        this.notesById = {}
        this.notesBySlug = {}
      }

      for (const note of this.allVaultsNotes) {
        if (note.id) this.notesById[note.id] = note
        // For slugs, prefix with vaultId to avoid collisions
        if (note.slug) {
          // In single-vault view with cross-vault suggestions,
          // don't overwrite current vault's slug mappings
          if (!this.allVaultsMode && this.notesBySlug[note.slug]) {
            // Only add vault-prefixed version
            this.notesBySlug[`${note.vaultId}:${note.slug}`] = note
          } else {
            this.notesBySlug[note.slug] = note
            // Also index with vault prefix for cross-vault linking
            this.notesBySlug[`${note.vaultId}:${note.slug}`] = note
          }
        }
      }

      if (this.allVaultsMode) {
        this._buildBacklinks(this.allVaultsNotes || [])
      }
    },

    /**
     * Build lookup indexes
     */
    _buildIndexes() {
      this.notesById = {}
      this.notesBySlug = {}
      for (const note of this.notes) {
        if (note.id) this.notesById[note.id] = note
        if (note.slug) this.notesBySlug[note.slug] = note
      }
      this._buildBacklinks(this.notes)

      // If all-vaults mode is active or cross-vault suggestions enabled,
      // also rebuild all-vaults indexes (merges on top)
      if (this.allVaultsNotesLoaded && (this.allVaultsMode || this.suggestFromAllVaults)) {
        this._buildAllVaultsIndexes()
      }
    },

    /**
     * Build backlinks and mentions from note content
     */
    _buildBacklinks(noteList = null) {
      this.backlinks = {}
      this.mentions = {}

      const notesSource = noteList || (this.allVaultsMode ? this.allVaultsNotes || [] : this.notes)
      if (!Array.isArray(notesSource) || notesSource.length === 0) return

      // Build a slug index that supports duplicate slugs across vaults
      const slugIndex = new Map() // slug -> [notes]
      for (const n of notesSource) {
        if (!n || !n.slug) continue
        const s = slugify(n.slug)
        if (!slugIndex.has(s)) slugIndex.set(s, [])
        slugIndex.get(s).push(n)
      }

      const resolveSlugTarget = (slug, sourceVaultId = null) => {
        const list = slugIndex.get(slug)
        if (!list || list.length === 0) return null
        if (list.length === 1) return list[0]
        if (sourceVaultId) {
          const same = list.find((n) => n.vaultId && String(n.vaultId) === String(sourceVaultId))
          if (same) return same
        }
        return list[0]
      }

      // Regex to find [[wikilinks]]
      const wikilinkRegex = /\[\[([^\]|]+)(?:\|[^\]]+)?\]\]/g
      const noteInfos = []

      // Map entity key -> note (for entity embed backlinks/mentions)
      const getEntityKey = (note) => {
        try {
          const fm = note?.frontmatter || {}
          const e = fm.entity || {}
          const type = (fm.entityType || e.type || '').toLowerCase()
          const id = fm.entityId || e.id || null
          if (!type || !id) return null
          return `${type}:${String(id).toLowerCase()}`
        } catch {
          return null
        }
      }
      const notesByEntity = new Map()
      for (const n of notesSource) {
        const key = getEntityKey(n)
        if (key) notesByEntity.set(key, n)
      }

      for (const note of notesSource) {
        const content = note.content || ''
        let match
        const parsed = parseMarkdown(content)
        const links = parsed.links
        const linkSlugs = new Set(
          (links || [])
            .filter((link) => link && link.kind === 'note' && link.targetSlug)
            .map((link) => link.targetSlug)
        )
        const linkEntities = new Set(
          (links || [])
            .filter((link) => link && link.kind === 'entity' && link.entityType && link.targetId)
            .map(
              (link) =>
                `${String(link.entityType).toLowerCase()}:${String(link.targetId).toLowerCase()}`
            )
        )
        const bodyText = parsed.body || content
        noteInfos.push({
          note,
          linkSlugs,
          linkEntities,
          normalizedBody: normalizeTerm(stripWikilinks(bodyText)),
          keywords: buildKeywordCandidates(note),
          parsedLinks: links || []
        })

        // Wikilink backlinks
        while ((match = wikilinkRegex.exec(content)) !== null) {
          const rawTarget = match[1].trim()
          // Skip external URLs and anchor-only/self-headings
          if (/^https?:\/\//i.test(rawTarget)) continue
          if (rawTarget.startsWith('#')) continue
          const hashPos = rawTarget.indexOf('#')
          const baseTarget = hashPos >= 0 ? rawTarget.slice(0, hashPos).trim() : rawTarget
          if (!baseTarget) continue
          const targetSlug = slugify(baseTarget)
          const targetNote = resolveSlugTarget(targetSlug, note.vaultId)

          if (targetNote && targetNote.id !== note.id) {
            if (!this.backlinks[targetNote.id]) {
              this.backlinks[targetNote.id] = []
            }

            const exists = this.backlinks[targetNote.id].some((b) => b.fromId === note.id)
            if (!exists) {
              this.backlinks[targetNote.id].push({
                fromId: note.id,
                fromVaultId: note.vaultId || this.activeVaultId || null,
                fromSlug: note.slug || '',
                fromTitle: note.title || 'Untitled',
                content: this._extractContext(content, match.index)
              })
            }
          }
        }

        // Entity backlinks via embed ([[type:id|Label]])
        for (const link of links || []) {
          if (!link || link.kind !== 'entity') continue
          const key = `${String(link.entityType || '').toLowerCase()}:${String(link.targetId || '').toLowerCase()}`
          const targetNote = notesByEntity.get(key)
          if (targetNote && targetNote.id !== note.id) {
            if (!this.backlinks[targetNote.id]) this.backlinks[targetNote.id] = []
            const exists = this.backlinks[targetNote.id].some((b) => b.fromId === note.id)
            if (!exists) {
              this.backlinks[targetNote.id].push({
                fromId: note.id,
                fromVaultId: note.vaultId || this.activeVaultId || null,
                fromSlug: note.slug || '',
                fromTitle: note.title || 'Untitled',
                content: this._extractContext(content, 0),
                matched: link.label || link.entityLabel || `${link.entityType}:${link.targetId}`
              })
            }
          }
        }
      }

      // Build mentions (unlinked references)
      // Strategy:
      // - Token matches for single-word keywords (fast)
      // - Phrase matches (multi-word) anchored by a "rare" token to avoid O(N_phrases * N_notes)
      const targetInfos = noteInfos.map((info) => ({
        id: info.note.id,
        slug: info.note.slug ? slugify(info.note.slug) : '',
        keywords: info.keywords,
        entityKey: getEntityKey(info.note)
      }))

      // Build keyword stats first to filter noisy single-word keywords
      const keywordStats = new Map() // norm -> count of targets that expose it
      const targetKeywordEntries = [] // collect to reuse
      for (const target of targetInfos) {
        for (const kw of target.keywords || []) {
          if (!kw || !kw.normalized) continue
          const norm = kw.normalized
          const isPhrase = norm.includes(' ')
          if (!isPhrase && !isTokenCandidate(norm)) continue
          targetKeywordEntries.push({ target, kw, norm, isPhrase })
          keywordStats.set(norm, (keywordStats.get(norm) || 0) + 1)
        }
      }

      // Build indexes:
      // - keywordIndex: single-word term -> [{ targetId, slug, entityKey, display }]
      // - phraseMap: phrase term -> [{ targetId, slug, entityKey, display }]
      const keywordIndex = new Map()
      const phraseMap = new Map()
      const NOISY_THRESHOLD = 5
      for (const entry of targetKeywordEntries) {
        const { target, kw, norm, isPhrase } = entry
        if (isPhrase) {
          if (!phraseMap.has(norm)) phraseMap.set(norm, [])
          phraseMap.get(norm).push({
            targetId: target.id,
            slug: target.slug,
            entityKey: target.entityKey,
            display: kw.display || norm
          })
          continue
        }
        // Skip overly common single-word keywords
        if ((keywordStats.get(norm) || 0) > NOISY_THRESHOLD) continue
        if (!keywordIndex.has(norm)) keywordIndex.set(norm, [])
        keywordIndex.get(norm).push({
          targetId: target.id,
          slug: target.slug,
          entityKey: target.entityKey,
          display: kw.display || norm
        })
      }

      // Anchor phrases by a rare token so we only test phrases when likely present.
      const phraseTokenStats = new Map() // token -> number of phrases containing it
      for (const phrase of phraseMap.keys()) {
        const uniqueTokens = new Set(String(phrase).split(' ').filter(isTokenCandidate))
        for (const token of uniqueTokens) {
          phraseTokenStats.set(token, (phraseTokenStats.get(token) || 0) + 1)
        }
      }
      const phraseIndex = new Map() // anchor token -> [{ phrase, candidates }]
      for (const [phrase, candidates] of phraseMap.entries()) {
        const tokens = String(phrase).split(' ').filter(isTokenCandidate)
        if (!tokens.length) continue
        let anchor = tokens[0]
        let bestScore = phraseTokenStats.get(anchor) || Infinity
        for (const token of tokens) {
          const score = phraseTokenStats.get(token) || Infinity
          if (score < bestScore || (score === bestScore && token.length > anchor.length)) {
            anchor = token
            bestScore = score
          }
        }
        if (!phraseIndex.has(anchor)) phraseIndex.set(anchor, [])
        phraseIndex.get(anchor).push({ phrase, candidates })
      }

      for (const source of noteInfos) {
        if (!source.note || !source.note.id) continue
        const bodyText = source.normalizedBody || ''
        if (!bodyText) continue
        const paddedBody = ` ${bodyText} `
        const tokens = Array.from(new Set(String(bodyText).split(' ').filter(isTokenCandidate)))
        const skipSlugs = source.linkSlugs
        const skipEntities = source.linkEntities
        const added = new Set()
        const sourceVaultId = source.note.vaultId || this.activeVaultId || null

        for (const token of tokens) {
          // Single-word mentions
          const candidates = keywordIndex.get(token)
          if (candidates && candidates.length) {
            for (const cand of candidates) {
              if (!cand.targetId || cand.targetId === source.note.id) continue
              if (cand.slug && skipSlugs.has(cand.slug)) continue
              if (cand.entityKey && skipEntities.has(cand.entityKey)) continue
              const key = `${source.note.id}->${cand.targetId}`
              if (added.has(key)) continue
              added.add(key)
              if (!this.mentions[cand.targetId]) this.mentions[cand.targetId] = []
              this.mentions[cand.targetId].push({
                fromId: source.note.id,
                fromVaultId: sourceVaultId,
                fromSlug: source.note.slug || '',
                fromTitle: source.note.title || 'Untitled',
                matched: cand.display
              })
            }
          }

          // Phrase mentions anchored on this token
          const phraseEntries = phraseIndex.get(token)
          if (!phraseEntries || phraseEntries.length === 0) continue
          for (const entry of phraseEntries) {
            const phrase = entry?.phrase
            const phraseCandidates = entry?.candidates || []
            if (!phrase || phraseCandidates.length === 0) continue
            if (!paddedBody.includes(` ${phrase} `)) continue
            for (const cand of phraseCandidates) {
              if (!cand.targetId || cand.targetId === source.note.id) continue
              if (cand.slug && skipSlugs.has(cand.slug)) continue
              if (cand.entityKey && skipEntities.has(cand.entityKey)) continue
              const key = `${source.note.id}->${cand.targetId}`
              if (added.has(key)) continue
              added.add(key)
              if (!this.mentions[cand.targetId]) this.mentions[cand.targetId] = []
              this.mentions[cand.targetId].push({
                fromId: source.note.id,
                fromVaultId: sourceVaultId,
                fromSlug: source.note.slug || '',
                fromTitle: source.note.title || 'Untitled',
                matched: cand.display
              })
            }
          }
        }
      }
    },

    /**
     * Extract context around a match for backlink preview
     */
    _extractContext(content, matchIndex) {
      const contextLength = 100
      const start = Math.max(0, matchIndex - contextLength)
      const end = Math.min(content.length, matchIndex + contextLength)
      let excerpt = content.slice(start, end)
      if (start > 0) excerpt = '...' + excerpt
      if (end < content.length) excerpt = excerpt + '...'
      return excerpt.replace(/\n/g, ' ').trim()
    },

    /**
     * Get backlinks for a note
     */
    getBacklinks(noteId) {
      return this.backlinks[noteId] || []
    },

    /**
     * Get mentions for a note
     */
    getMentions(noteId) {
      return this.mentions[noteId] || []
    },

    /**
     * Create a new note
     */
    async createNote(draft, vaultId = null) {
      // Resolve target vault: explicit vault > UI action vault in all-vaults > active vault
      const targetVaultId =
        vaultId ||
        (this.allVaultsMode ? this.uiActionVaultId || this.activeVaultId : this.activeVaultId)
      if (!targetVaultId) throw new Error('No active or target vault')

      // Ensure ownerId is set from user context
      const draftWithOwner = {
        ...draft,
        ownerId: draft.ownerId || this.userContext?.userId || null
      }
      const draftPerm =
        draftWithOwner.permissions ||
        this._getDefaultNotePermission(this.userContext, targetVaultId)
      const note = await NoteService.createNote(targetVaultId, {
        ...draftWithOwner,
        permissions: draftPerm,
        frontmatter: { ...(draftWithOwner.frontmatter || {}), permissions: draftPerm }
      })
      if (targetVaultId === this.activeVaultId) {
        this.notes.push(note)
      }

      // Also add to allVaultsNotes if loaded (for all-vaults mode)
      if (this.allVaultsNotesLoaded) {
        // Add vaultId and vaultName for all-vaults display
        const noteWithVault = {
          ...note,
          vaultId: targetVaultId,
          vaultName: this.getVaultName(targetVaultId) || this.activeVault?.name || targetVaultId
        }
        this.allVaultsNotes.push(noteWithVault)
      }

      this._buildIndexes()

      // Auto-create any missing linked notes in this vault
      // Ensure the note carries the vaultId so stub/entity creation targets the same vault
      await this.ensureLinkedNotesExist({ ...note, vaultId: targetVaultId })

      // Increment version to trigger graph rebuild AFTER linked notes are created
      this.notesVersion++

      return note
    },

    /**
     * Create a linked note (for WikilinkAutocomplete "Create" option)
     * @param {Object} _ctx - Context (unused but kept for API compat with old notesStore)
     * @param {Object} options - { title, slug, permissions }
     * @returns {Object|null} The created note
     */
    async createLinkedNote(
      _ctx,
      { title, slug, permissions: permOverride, vaultId, folder = null } = {}
    ) {
      const targetVaultId =
        vaultId ||
        (this.allVaultsMode ? this.uiActionVaultId || this.activeVaultId : this.activeVaultId)
      if (!targetVaultId) throw new Error('No target vault')

      const baseSlug = slug || slugify(title || 'note')
      let finalSlug = baseSlug
      let i = 2
      while (this.notesBySlug[finalSlug]) {
        finalSlug = `${baseSlug}-${i}`
        i += 1
      }

      // If a note with this slug already exists, return it
      if (this.notesBySlug[finalSlug]) {
        return this.notesBySlug[finalSlug]
      }

      const context = this.userContext || {}
      const permissions = permOverride || this._getDefaultNotePermission(context, targetVaultId)

      const draft = {
        title: title || finalSlug,
        slug: finalSlug,
        content: '',
        tags: [],
        aliases: [],
        permissions,
        ownerId: context.userId || null,
        frontmatter: { permissions, tags: [], aliases: [] }
      }

      // Create in the target vault (may differ from active vault in all-vaults mode)
      const targetFolder =
        folder != null
          ? folder
          : this.activeNote?.folder || this.notesById[this.activeNoteId]?.folder || ''
      const createKey = `${targetVaultId}::${targetFolder || ''}::${finalSlug}`
      if (!this._pendingLinkedCreates) {
        this._pendingLinkedCreates = new Map()
      }
      if (this._pendingLinkedCreates.has(createKey)) {
        return this._pendingLinkedCreates.get(createKey)
      }

      const createPromise = (async () => {
        const { note, created } = await NoteService.createLinkedNoteIfMissing(targetVaultId, {
          ...draft,
          folder: targetFolder || '',
          ownerId: context.userId || null
        })

        // Update local state
        if (created && note && targetVaultId === this.activeVaultId) {
          this.notes.push(note)
        }
        // Always add to all-vaults notes list if loaded
        if (created && note && this.allVaultsNotesLoaded) {
          const noteWithVault = {
            ...note,
            vaultId: targetVaultId,
            vaultName: this.getVaultName(targetVaultId)
          }
          this.allVaultsNotes.push(noteWithVault)
        }

        this._buildIndexes()
        if (created) this.notesVersion++
        return note || null
      })()

      this._pendingLinkedCreates.set(createKey, createPromise)
      try {
        return await createPromise
      } finally {
        this._pendingLinkedCreates.delete(createKey)
      }
    },

    /**
     * Create an entity note in a structured folder with proper frontmatter.
     * @param {Object} params
     * @param {string} params.entityType
     * @param {string|number} params.entityId
     * @param {string} [params.label]
     * @param {string} [params.vaultId]
     * @returns {Object|null}
     */
    async createEntityNote({ entityType, entityId, label, vaultId } = {}) {
      const context = this.userContext || {}
      let targetVaultId =
        vaultId ||
        (this.allVaultsMode ? this.uiActionVaultId || this.activeVaultId : this.activeVaultId)
      if (!targetVaultId && Array.isArray(this.allVaultsNotes) && this.activeNoteId) {
        const found = this.allVaultsNotes.find((n) => n && n.id === this.activeNoteId && n.vaultId)
        if (found?.vaultId) targetVaultId = found.vaultId
      }
      if (!entityType || !entityId || !targetVaultId) return null

      // Try to resolve entity metadata
      let summary = null
      try {
        const registry = getProviderRegistry()
        const provider = registry.get(entityType)
        if (provider && typeof provider.getById === 'function') {
          summary = await provider.getById(entityId, context)
          if (!summary && typeof provider.preload === 'function') {
            await provider.preload(context)
            summary = await provider.getById(entityId, context)
          }
        }
      } catch {}

      let resolvedLabel = label || summary?.label || `${entityType}:${entityId}`
      if (entityType === 'spell' && resolvedLabel === `${entityType}:${entityId}`) {
        resolvedLabel = humanizeEntityId(entityId)
      }
      const image = summary?.image || null
      const description = normalizeDescriptionToMarkdownText(
        summary?.subtitle || summary?.data?.description || ''
      )
      let backstory = ''
      try {
        const d = summary?.data || {}
        backstory = normalizeDescriptionToMarkdownText(
          d.backstory || d.biography || d.bio || d.background || d.story || description || ''
        )
      } catch {
        backstory = description || ''
      }

      // Build slug and folder
      const baseSlug = slugify(resolvedLabel || `${entityType}-${entityId}`)
      let finalSlug = baseSlug
      let i = 2
      while (this.notesBySlug[finalSlug]) {
        finalSlug = `${baseSlug}-${i}`
        i += 1
      }
      const enableEntityFolders = (this.settings?.autoCreateEntityFolders ?? true) === true
      const folder = enableEntityFolders ? getEntityFolder(entityType) : undefined

      const permissions = this._getDefaultNotePermission(context, targetVaultId)
      const fm = {
        permissions,
        image: image || undefined,
        entityType: entityType,
        entityId: entityId,
        entity: {
          type: entityType,
          id: entityId,
          label: resolvedLabel,
          image: image || null,
          description: description || ''
        }
      }
      const contentParts = []
      const fullText = String(backstory || '').trim()
      const headingLike = /^#{1,6}\s/.test(fullText)
      const linkText = `![[${entityType}:${entityId}|${resolvedLabel}|compact]]`
      const paragraph = fullText
        ? headingLike
          ? `${linkText}\n\n${fullText}`
          : `${linkText}\n\n${fullText}`
        : `${linkText}`
      contentParts.push(paragraph)
      const content = contentParts.filter(Boolean).join('\n\n')

      const note = await NoteService.createNote(targetVaultId, {
        title: resolvedLabel,
        slug: finalSlug,
        content,
        ...(folder ? { folder } : {}),
        permissions,
        ownerId: context.userId || null,
        createdAt: new Date(),
        frontmatter: fm
      })

      if (note) {
        // Insert into local state
        if (targetVaultId === this.activeVaultId) {
          this.notes.unshift(note)
        }
        if (this.allVaultsNotesLoaded) {
          this.allVaultsNotes.unshift({
            ...note,
            vaultId: targetVaultId,
            vaultName: this.getVaultName(targetVaultId)
          })
        }
        this._buildIndexes()
        this.notesVersion++
        try {
          await this.ensureFolderMetaFor(targetVaultId, folder)
        } catch {}
      }

      return note || null
    },

    /**
     * Update a note
     * @param {string} noteId - The note ID
     * @param {Object} updates - The updates to apply
     * @param {string} [vaultId] - Optional vault ID (required in all-vaults mode)
     */
    async updateNote(noteId, updates, vaultId = null, options = {}) {
      // Determine which vault to use
      const targetVaultId = vaultId || this.activeVaultId
      if (!targetVaultId) throw new Error('No vault specified')

      // Store-level validations for programmatic saves
      try {
        const existingLocal = this.notesById?.[noteId] || this.notes.find((n) => n.id === noteId)
        const nextPerms = String(
          updates?.permissions || existingLocal?.permissions || 'PRIVATE'
        ).toUpperCase()
        // Normalize permissions in updates so downstream stays consistent
        if (updates && updates.permissions) updates.permissions = nextPerms

        if (nextPerms === 'CAMPAIGN') {
          const candidate = updates?.campaignIds ?? existingLocal?.campaignIds ?? []
          const list = Array.isArray(candidate)
            ? Array.from(new Set(candidate.filter(Boolean)))
            : []
          const cid =
            updates?.campaignId ?? existingLocal?.campaignId ?? (list.length > 0 ? list[0] : null)
          if (!cid) {
            throw new Error('Select at least one campaign')
          }
          // Normalize both fields for downstream persistence and rule compatibility
          updates.campaignIds = list
          if (!updates.campaignId) updates.campaignId = cid
        }
        if (nextPerms === 'USER_SPECIFIC') {
          const listRaw = updates?.allowedUserIds ?? existingLocal?.allowedUserIds ?? []
          const list = Array.isArray(listRaw) ? Array.from(new Set(listRaw.filter(Boolean))) : []
          if (!list.length) {
            throw new Error('Pick at least one user')
          }
          // Ensure normalized in updates so it persists
          updates.allowedUserIds = list
        }
      } catch (e) {
        // Surface friendly message and abort save
        this.error = e?.message || 'Invalid note permissions metadata'
        throw e
      }

      const skipGraph = !!options?.skipGraph
      const preserveUpdatedAt = skipGraph || !!options?.preserveUpdatedAt

      const updated = await NoteService.updateNote(targetVaultId, noteId, updates)

      // Update in current vault's notes
      const idx = this.notes.findIndex((n) => n.id === noteId)
      let existing = idx !== -1 ? this.notes[idx] : null
      let existingAll = null
      let prevSnapshot = existing ? { ...existing } : null
      let merged = existing ? { ...existing, ...updated } : { ...updated }
      if (preserveUpdatedAt && existing) {
        if (Object.prototype.hasOwnProperty.call(existing, 'updatedAt')) {
          merged.updatedAt = existing.updatedAt
        } else if (Object.prototype.hasOwnProperty.call(merged, 'updatedAt')) {
          delete merged.updatedAt
        }
      }
      if (idx !== -1 && existing) {
        Object.assign(existing, merged)
      }

      // Also update in allVaultsNotes if loaded
      if (this.allVaultsNotesLoaded) {
        const allIdx = this.allVaultsNotes.findIndex((n) => n.id === noteId)
        if (allIdx !== -1) {
          existingAll = this.allVaultsNotes[allIdx]
          if (!prevSnapshot && existingAll) {
            // Use all-vaults snapshot if note isn't in active list
            prevSnapshot = { ...existingAll }
          }
          const mergedAll = existingAll ? { ...existingAll, ...updated } : { ...updated }
          if (preserveUpdatedAt && existingAll) {
            if (Object.prototype.hasOwnProperty.call(existingAll, 'updatedAt')) {
              mergedAll.updatedAt = existingAll.updatedAt
            } else if (Object.prototype.hasOwnProperty.call(mergedAll, 'updatedAt')) {
              delete mergedAll.updatedAt
            }
          }
          Object.assign(existingAll, mergedAll)
        }
      }

      const baseForCompare = prevSnapshot || existingAll || existing
      const arrayEqualNormalized = (a = [], b = []) => {
        if (!Array.isArray(a) || !Array.isArray(b)) return false
        if (a.length !== b.length) return false
        const norm = (arr) =>
          arr
            .map((v) =>
              String(v || '')
                .trim()
                .toLowerCase()
            )
            .filter(Boolean)
            .sort()
        const na = norm(a)
        const nb = norm(b)
        if (na.length !== nb.length) return false
        for (let i = 0; i < na.length; i++) {
          if (na[i] !== nb[i]) return false
        }
        return true
      }
      const linksEqual = (a = [], b = []) => {
        if (!Array.isArray(a) || !Array.isArray(b)) return false
        const normSet = (links) => {
          const set = new Set()
          for (const l of links || []) {
            if (!l || (l.kind !== 'note' && l.kind !== 'entity')) continue
            const key =
              l.kind === 'note'
                ? `n:${String(l.targetSlug || '').toLowerCase()}`
                : `e:${String(l.entityType || '').toLowerCase()}:${String(
                    l.targetId || ''
                  ).toLowerCase()}`
            if (key) set.add(key)
          }
          return set
        }
        const sa = normSet(a)
        const sb = normSet(b)
        if (sa.size !== sb.size) return false
        for (const key of sa) {
          if (!sb.has(key)) return false
        }
        return true
      }

      const titleChanged = baseForCompare
        ? updated.title !== undefined
          ? baseForCompare.title !== updated.title
          : false
        : true
      const slugChanged = baseForCompare
        ? updated.slug !== undefined
          ? baseForCompare.slug !== updated.slug
          : false
        : true
      const folderChanged = baseForCompare
        ? updated.folder !== undefined
          ? baseForCompare.folder !== updated.folder
          : false
        : true
      const iconChanged = baseForCompare
        ? updated.icon !== undefined
          ? baseForCompare.icon !== updated.icon
          : false
        : true
      const tagsChanged = Array.isArray(updated.tags)
        ? !arrayEqualNormalized(updated.tags, baseForCompare?.tags || [])
        : false
      const aliasesChanged = Array.isArray(updated.aliases)
        ? !arrayEqualNormalized(updated.aliases, baseForCompare?.aliases || [])
        : false
      const baseLinks = Array.isArray(baseForCompare?.links)
        ? baseForCompare.links
        : parseMarkdown(baseForCompare?.content || '').links
      const linksChanged = Array.isArray(updated.links)
        ? !linksEqual(updated.links, baseLinks || [])
        : false

      const indexRelevantChanged =
        titleChanged || slugChanged || tagsChanged || aliasesChanged || linksChanged
      if (indexRelevantChanged) {
        this._buildIndexes()
      }

      const shouldSkipGraph = skipGraph || !!options?.skipGraphNext || this._suppressNextGraph
      if (this._suppressNextGraph) this._suppressNextGraph = false

      if (!shouldSkipGraph) {
        let createdLinked = false
        if (linksChanged) {
          // Auto-create any missing linked notes and entity notes in the target vault
          try {
            const created = await this.ensureLinkedNotesExist({ ...merged, vaultId: targetVaultId })
            createdLinked = !!created
          } catch {}
        }

        if (linksChanged) {
          // Run orphaned stub cleanup when links change so deletions via autosave
          // work even when editing cross-vault in All Vaults mode
          await this.cleanupOrphanedStubsInVault(targetVaultId)
        }

        const graphRelevantChanged =
          indexRelevantChanged || folderChanged || titleChanged || slugChanged
        if (!createdLinked && graphRelevantChanged) {
          // Increment version to trigger graph rebuild AFTER linked notes are created
          this.notesVersion++
        }
      }

      return updated
    },

    /**
     * Move a note to a different vault (creates a new note in target, deletes source)
     * @param {Object} note - The note to move
     * @param {string} targetVaultId - Destination vault ID
     * @param {string} [targetFolder] - Destination folder path
     * @returns {Object|null} The newly created note in the target vault
     */
    async moveNoteToVault(note, targetVaultId, targetFolder = '') {
      if (!note?.id) throw new Error('note required')
      if (!targetVaultId) throw new Error('targetVaultId required')

      const sourceVaultId = note.vaultId || this.activeVaultId
      if (!sourceVaultId) throw new Error('sourceVaultId required')
      if (sourceVaultId === targetVaultId) return note

      const draft = {
        title: note.title || 'Untitled',
        slug: note.slug || undefined,
        content: note.content || '',
        folder: targetFolder || '',
        icon: note.icon || null,
        frontmatter: note.frontmatter || {},
        tags: Array.isArray(note.tags) ? note.tags : [],
        aliases: Array.isArray(note.aliases) ? note.aliases : [],
        permissions: note.permissions || note.frontmatter?.permissions || 'PRIVATE',
        ownerId: note.ownerId || this.userContext?.userId || null
      }

      const created = await NoteService.createNote(targetVaultId, draft)
      await NoteService.deleteNote(sourceVaultId, note.id)

      if (sourceVaultId === this.activeVaultId) {
        this.notes = this.notes.filter((n) => n.id !== note.id)
      }
      if (targetVaultId === this.activeVaultId) {
        this.notes.push(created)
      }

      if (this.allVaultsNotesLoaded) {
        this.allVaultsNotes = (this.allVaultsNotes || []).filter((n) => n.id !== note.id)
        this.allVaultsNotes.push({
          ...created,
          vaultId: targetVaultId,
          vaultName: this.getVaultName(targetVaultId)
        })
      }

      this._buildIndexes()
      this.notesVersion++

      if (this.activeNoteId === note.id && created?.id) {
        this.setActiveNote(created.id)
      }

      return created
    },

    /**
     * Move a folder (and its notes) to a different vault and folder path.
     * @param {Object} params
     * @param {string} params.sourceVaultId
     * @param {string} params.sourcePath
     * @param {string} params.targetVaultId
     * @param {string} params.targetPath
     */
    async moveFolderToVault({ sourceVaultId, sourcePath, targetVaultId, targetPath }) {
      if (!sourceVaultId || !sourcePath) throw new Error('sourceVaultId and sourcePath required')
      if (!targetVaultId) throw new Error('targetVaultId required')

      const notes = await NoteService.listNotes(sourceVaultId)
      const toMove = (notes || []).filter((n) => {
        const folder = n.folder || ''
        return folder === sourcePath || isChildOf(folder, sourcePath)
      })

      if (!toMove.length) return []

      const normalizeTargetRoot = (path) => (path || '').replace(/^\/+/, '').replace(/\/+$/, '')
      const baseTarget = normalizeTargetRoot(targetPath || '')
      const sourceFolderName = getFolderName(sourcePath)
      const targetRoot = joinFolderPath([baseTarget, sourceFolderName])

      const movedNotes = []
      for (const note of toMove) {
        const folder = note.folder || ''
        let relative = ''
        if (folder === sourcePath) {
          relative = ''
        } else if (folder.startsWith(sourcePath + '/')) {
          relative = folder.slice(sourcePath.length + 1)
        }
        const nextFolder = joinFolderPath([targetRoot, relative])
        const draft = {
          title: note.title || 'Untitled',
          slug: note.slug || undefined,
          content: note.content || '',
          folder: nextFolder || '',
          icon: note.icon || null,
          frontmatter: note.frontmatter || {},
          tags: Array.isArray(note.tags) ? note.tags : [],
          aliases: Array.isArray(note.aliases) ? note.aliases : [],
          permissions: note.permissions || note.frontmatter?.permissions || 'PRIVATE',
          ownerId: note.ownerId || this.userContext?.userId || null
        }

        const created = await NoteService.createNote(targetVaultId, draft)
        movedNotes.push({ source: note, created })
      }

      for (const note of toMove) {
        await NoteService.deleteNote(sourceVaultId, note.id)
      }

      // Move folder metadata (icons/explicit flags) to the target vault.
      try {
        const sourceVault =
          sourceVaultId === this.activeVaultId
            ? this.activeVault
            : (this.vaults || []).find((v) => v.id === sourceVaultId) ||
              (await VaultService.getVault(sourceVaultId))
        const targetVault =
          targetVaultId === this.activeVaultId
            ? this.activeVault
            : (this.vaults || []).find((v) => v.id === targetVaultId) ||
              (await VaultService.getVault(targetVaultId))

        const sourceMeta = { ...(sourceVault?.folderMeta || {}) }
        const targetMeta = { ...(targetVault?.folderMeta || {}) }
        const movedMeta = {}
        for (const [path, meta] of Object.entries(sourceMeta)) {
          if (path === sourcePath || path.startsWith(sourcePath + '/')) {
            const suffix = path === sourcePath ? '' : path.slice(sourcePath.length + 1)
            const nextPath = joinFolderPath([targetRoot, suffix])
            if (nextPath) {
              movedMeta[nextPath] = { ...(targetMeta[nextPath] || {}), ...(meta || {}) }
            }
          }
        }
        if (Object.keys(movedMeta).length > 0) {
          const mergedMeta = { ...targetMeta, ...movedMeta }
          await VaultService.updateVault(targetVaultId, { folderMeta: mergedMeta })

          if (targetVaultId === this.activeVaultId && this.activeVault) {
            this.activeVault = { ...this.activeVault, folderMeta: mergedMeta }
          }
          const tIdx = (this.vaults || []).findIndex((v) => v.id === targetVaultId)
          if (tIdx >= 0) {
            const v = this.vaults[tIdx]
            this.vaults.splice(tIdx, 1, { ...v, folderMeta: mergedMeta })
          }
        } else if (targetRoot) {
          await this.ensureFolderMetaFor(targetVaultId, targetRoot)
        }
      } catch {
        if (targetRoot) {
          await this.ensureFolderMetaFor(targetVaultId, targetRoot)
        }
      }

      await this.deleteFolderMetaFor(sourceVaultId, sourcePath)

      if (sourceVaultId === this.activeVaultId) {
        this.notes = this.notes.filter((n) => !toMove.some((m) => m.id === n.id))
      }
      if (targetVaultId === this.activeVaultId) {
        for (const item of movedNotes) {
          if (item?.created) this.notes.push(item.created)
        }
      }

      if (this.allVaultsNotesLoaded) {
        const removedIds = new Set(toMove.map((n) => n.id))
        this.allVaultsNotes = (this.allVaultsNotes || []).filter((n) => !removedIds.has(n.id))
        for (const item of movedNotes) {
          const created = item.created
          if (created) {
            this.allVaultsNotes.push({
              ...created,
              vaultId: targetVaultId,
              vaultName: this.getVaultName(targetVaultId)
            })
          }
        }
      }

      this._buildIndexes()
      this.notesVersion++

      return movedNotes
    },

    /**
     * Delete orphaned stub notes (empty content, no backlinks)
     * Called after note updates to clean up auto-created stubs that are no longer linked
     */
    async cleanupOrphanedStubs() {
      if (!this.activeVaultId) return
      if (!this.userContext?.userId) return
      const enableAutoCreate = (this.settings?.autoCreateWikiNotes ?? false) === true
      if (!enableAutoCreate) return

      // Get the currently active note ID to protect it from deletion
      const activeNoteId = this.activeNoteId
      const currentUserId = this.userContext.userId

      // Find notes that are stubs (empty content) with no backlinks
      const orphanedStubs = this.notes.filter((note) => {
        // Never delete the currently active note
        if (note.id === activeNoteId) return false

        // Only delete notes owned by the current user (permission check)
        if (note.ownerId && note.ownerId !== currentUserId) return false

        // Must have empty or whitespace-only content
        const isEmpty = !note.content || String(note.content).trim() === ''
        if (!isEmpty) return false

        // Must have no backlinks from other notes
        const backlinks = this.backlinks[note.id] || []
        if (backlinks.length > 0) return false

        // Don't delete notes that have tags, aliases, or other metadata indicating user intent
        const hasTags = Array.isArray(note.tags) && note.tags.length > 0
        const hasAliases = Array.isArray(note.aliases) && note.aliases.length > 0
        if (hasTags || hasAliases) return false

        // Don't delete notes with a custom title (user renamed it)
        // Stub notes have title matching their slug
        const titleSlug = slugify(note.title || '')
        const isDefaultTitle = titleSlug === note.slug
        if (!isDefaultTitle) return false

        return true
      })

      if (!orphanedStubs.length) return

      // Collect IDs to delete
      const idsToDelete = orphanedStubs.map((s) => s.id)

      // Delete from Firestore (parallel for speed)
      await Promise.all(
        idsToDelete.map((id) =>
          NoteService.deleteNote(this.activeVaultId, id).catch((err) => {
            console.warn('[vaultStore] Failed to delete orphaned stub:', id, err)
          })
        )
      )

      // Remove all deleted notes from local state in one operation
      const deleteSet = new Set(idsToDelete)
      this.notes = this.notes.filter((n) => !deleteSet.has(n.id))

      // Clear active note if it was deleted
      if (deleteSet.has(this.activeNoteId)) {
        this.activeNoteId = null
      }

      // Rebuild indexes and trigger reactivity
      this._buildIndexes()
      this.notesVersion++
    },

    /**
     * Cleanup orphaned stub notes in a specific vault (used in all-vaults mode)
     * Deletes empty notes with no backlinks across all notes, only if owned by current user.
     */
    async cleanupOrphanedStubsInVault(vaultId, override = null) {
      const currentUserId = this.userContext?.userId
      if (!vaultId || !currentUserId) return
      const settings =
        vaultId === this.activeVaultId
          ? this.settings || {}
          : (this.vaults || []).find((v) => v && v.id === vaultId)?.settings || {}
      const enableAutoCreate = (settings?.autoCreateWikiNotes ?? false) === true
      if (!enableAutoCreate) return

      // Ensure we have all-vaults notes to check backlinks globally
      if (!this.allVaultsNotesLoaded) {
        try {
          await this.loadAllVaultsNotes()
        } catch {}
      }

      const allNotes = Array.isArray(this.allVaultsNotes) ? this.allVaultsNotes : []
      const vaultNotes = allNotes.filter((n) => n && n.vaultId === vaultId)
      if (!vaultNotes.length) return

      // Build backlinks by slug across all notes
      const backlinksBySlug = new Map()
      const wikilinkRegex = /\[\[([^\]|]+)(?:\|[^\]]+)?\]\]/g
      const toSlug = (s) => slugify(String(s || ''))
      for (const note of allNotes) {
        const isOverride = override && override.noteId && note.id === override.noteId
        const content = isOverride ? String(override.content || '') : String(note?.content || '')
        let m
        while ((m = wikilinkRegex.exec(content)) !== null) {
          const raw = (m[1] || '').trim()
          // Skip external URLs embedded via wikilinks
          if (/^https?:\/\//i.test(raw)) continue
          if (!raw) continue
          const s = toSlug(raw)
          if (!backlinksBySlug.has(s)) backlinksBySlug.set(s, 0)
          backlinksBySlug.set(s, backlinksBySlug.get(s) + 1)
        }
      }

      // Get the currently active note ID to protect it from deletion
      const activeNoteId = this.activeNoteId

      const idsToDelete = []
      for (const note of vaultNotes) {
        // Never delete the currently active note (even if empty)
        if (note.id === activeNoteId) continue
        // Only delete notes owned by the current user
        if (note.ownerId && note.ownerId !== currentUserId) continue
        const isEmpty = !note.content || String(note.content).trim() === ''
        if (!isEmpty) continue
        const hasTags = Array.isArray(note.tags) && note.tags.length > 0
        const hasAliases = Array.isArray(note.aliases) && note.aliases.length > 0
        if (hasTags || hasAliases) continue
        const titleSlug = toSlug(note.title || '')
        const isDefaultTitle = titleSlug === note.slug
        if (!isDefaultTitle) continue
        const inbound = backlinksBySlug.get(note.slug) || 0
        if (inbound > 0) continue
        idsToDelete.push(note.id)
      }

      if (!idsToDelete.length) return

      await Promise.all(
        idsToDelete.map((id) =>
          NoteService.deleteNote(vaultId, id).catch((err) =>
            console.warn('[vaultStore] Failed to delete orphaned stub (vault):', id, err)
          )
        )
      )

      // Update local state arrays
      const delSet = new Set(idsToDelete)
      if (vaultId === this.activeVaultId) {
        this.notes = (this.notes || []).filter((n) => !delSet.has(n.id))
      }
      this.allVaultsNotes = (this.allVaultsNotes || []).filter((n) => !delSet.has(n.id))
      if (delSet.has(this.activeNoteId)) {
        this.activeNoteId = null
      }
      this._buildIndexes()
      this.notesVersion++
    },

    /**
     * Ensure all wikilinked notes exist (auto-create stubs for missing links)
     * @param {Object} note - The note to check for links
     */
    async ensureLinkedNotesExist(note) {
      if (!note) return
      if (!this._ensureLinkedNotesQueue) {
        this._ensureLinkedNotesQueue = { running: false, pending: null }
      }
      this._ensureLinkedNotesQueue.pending = note
      let createdAny = false
      const shouldSuppressGraph = note?._suppressGraph === true
      if (this._ensureLinkedNotesQueue.running) return
      this._ensureLinkedNotesQueue.running = true
      this.linkingNotes = true
      try {
        while (this._ensureLinkedNotesQueue.pending) {
          const next = this._ensureLinkedNotesQueue.pending
          this._ensureLinkedNotesQueue.pending = null
          const created = await this._ensureLinkedNotesExistInternal(next)
          if (created) createdAny = true
        }
      } finally {
        this._ensureLinkedNotesQueue.running = false
        this.linkingNotes = false
      }
      if (shouldSuppressGraph && createdAny) {
        this._suppressNextGraph = true
      }
      return createdAny
    },

    async _ensureLinkedNotesExistInternal(note) {
      if (!note) return
      if (!this.userContext?.userId) return
      let targetVaultId = note.vaultId || this.activeVaultId
      // In all-vaults scenarios, resolve vault by note id if missing or mismatched
      if ((!targetVaultId || this.allVaultsMode) && note.id && Array.isArray(this.allVaultsNotes)) {
        const found = this.allVaultsNotes.find((n) => n && n.id === note.id && n.vaultId)
        if (found?.vaultId) targetVaultId = found.vaultId
      }
      if (!targetVaultId) return

      // Always parse fresh content to get current links
      // Don't use note.links as it may be stale from initial load
      const links = parseMarkdown(note.content || '').links
      const context = this.userContext || {}
      const createdNotes = []

      // Auto-create empty notes for missing wiki links (guarded by vault setting)
      const vaultSettings =
        targetVaultId === this.activeVaultId
          ? this.settings || {}
          : (this.vaults || []).find((v) => v && v.id === targetVaultId)?.settings || {}
      const enableAutoCreateWikiNotes = (vaultSettings?.autoCreateWikiNotes ?? true) === true
      const canCheckExisting =
        targetVaultId === this.activeVaultId || this.allVaultsNotesLoaded === true

      if (enableAutoCreateWikiNotes && canCheckExisting) {
        const noteLinks = (links || []).filter((l) => l && l.kind === 'note' && l.targetSlug)
        if (noteLinks.length) {
          const targetNotes =
            targetVaultId === this.activeVaultId
              ? this.notes || []
              : (this.allVaultsNotes || []).filter((n) => n && n.vaultId === targetVaultId)

          const existingSlugSet = new Set(
            targetNotes.map((n) => String(n?.slug || '').toLowerCase()).filter((s) => s.length > 0)
          )
          const currentSlug = String(note.slug || '').toLowerCase()
          const pendingSlugs = new Set()
          const drafts = []
          const baseFolder = note.folder || ''
          const permissions =
            note.permissions ||
            note.frontmatter?.permissions ||
            this._getDefaultNotePermission(context, targetVaultId)

          for (const link of noteLinks) {
            const rawSlug = String(link.targetSlug || '').toLowerCase()
            if (!rawSlug) continue
            if (rawSlug === currentSlug) continue
            if (existingSlugSet.has(rawSlug)) continue
            if (pendingSlugs.has(rawSlug)) continue

            pendingSlugs.add(rawSlug)
            const title =
              String(link.targetTitle || '').trim() || String(link.label || '').trim() || rawSlug

            drafts.push({
              title,
              slug: rawSlug,
              content: '',
              folder: baseFolder || '',
              permissions,
              ownerId: context.userId || null,
              frontmatter: { permissions, tags: [], aliases: [] }
            })
          }

          if (drafts.length) {
            try {
              const created = await NoteService.createNotesBatch(targetVaultId, drafts)
              if (created?.length) {
                createdNotes.push(...created)
              }
            } catch (err) {
              console.warn('[vaultStore] Failed to create linked notes batch:', err)
            }
          }
        }
      }

      // Additionally, ensure notes exist for typed entity links (e.g., character:ID)
      try {
        const registry = getProviderRegistry()
        const skipEntityTypes = new Set(['image', 'img', 'media', 'asset', 'file'])
        const entityLinks = (links || []).filter((l) => {
          if (!l || l.kind !== 'entity') return false
          const type = String(l.entityType || '').toLowerCase()
          if (!type) return false
          if (skipEntityTypes.has(type)) return false
          return true
        })
        const makeEntityKey = (t, id) => `${String(t || '').toLowerCase()}:${String(id || '')}`

        // Build existing entity mapping within current vault
        const existingByEntity = new Map()
        ;(this.notes || []).forEach((n) => {
          const fm = n?.frontmatter || {}
          const e = fm.entity || {}
          const type = (fm.entityType || e.type || '').toLowerCase()
          const id = fm.entityId || e.id || null
          if (type && id) existingByEntity.set(makeEntityKey(type, id), n)
        })

        for (const elink of entityLinks) {
          const type = elink.entityType
          const id = elink.targetId
          if (!type || !id) continue
          const ekey = makeEntityKey(type, id)
          if (existingByEntity.has(ekey)) continue

          // Resolve metadata if possible
          let summary = null
          try {
            const provider = registry.get(type)
            if (provider && typeof provider.getById === 'function') {
              summary = await provider.getById(id, context)
              if (!summary && typeof provider.preload === 'function') {
                await provider.preload(context)
                summary = await provider.getById(id, context)
              }
            }
          } catch {}

          // Prefer canonical entity label if provided via triple-pipe entity link
          let label = elink.entityLabel || summary?.label || elink.label || `${type}:${id}`
          if (type === 'spell' && label === `${type}:${id}`) {
            label = humanizeEntityId(id)
          }
          const image = summary?.image || null
          const description = normalizeDescriptionToMarkdownText(
            summary?.subtitle || summary?.data?.description || ''
          )
          // Prefer character backstory/biography if available
          let backstory = ''
          try {
            const d = summary?.data || {}
            backstory = normalizeDescriptionToMarkdownText(
              d.backstory || d.biography || d.bio || d.background || d.story || description || ''
            )
          } catch {
            backstory = description || ''
          }

          // Build unique slug from label
          const baseSlug = slugify(label || `${type}-${id}`)
          let finalSlug = baseSlug
          let i = 2
          while (this.notesBySlug[finalSlug]) {
            finalSlug = `${baseSlug}-${i}`
            i += 1
          }

          const permissions =
            note.permissions ||
            note.frontmatter?.permissions ||
            this._getDefaultNotePermission(context, targetVaultId)
          const enableEntityFolders = (this.settings?.autoCreateEntityFolders ?? true) === true
          const folder = enableEntityFolders ? getEntityFolder(type) : undefined
          const fm = {
            permissions,
            image: image || undefined,
            entityType: type,
            entityId: id,
            entity: { type, id, label, image: image || null, description: description || '' }
          }
          const contentParts = []
          // Display text should ignore mode keywords
          const isMode = (s) => {
            const v = String(s || '').toLowerCase()
            return v === 'inline' || v === 'compact' || v === 'card' || v === 'avatar'
          }
          const displayText = !isMode(elink.label) && elink.label ? elink.label : label
          const linkText = `![[${type}:${id}${displayText ? `|${displayText}` : ''}|compact]]`
          const fullText = String(backstory || '').trim()
          const headingLike = /^#{1,6}\s/.test(fullText)
          const paragraph = fullText
            ? headingLike
              ? `${linkText}\n\n${fullText}`
              : `${linkText}\n\n${fullText}`
            : `${linkText}`
          contentParts.push(paragraph)
          const content = contentParts.filter(Boolean).join('\n\n')

          try {
            const created = await NoteService.createNote(targetVaultId, {
              title: label,
              slug: finalSlug,
              content,
              folder,
              permissions,
              ownerId: context.userId || null,
              createdAt: note.createdAt || new Date(),
              frontmatter: fm
            })
            if (created) {
              createdNotes.push(created)
              existingByEntity.set(ekey, created)
              // Ensure folder metadata exists in this vault for cross-device sync
              try {
                await this.ensureFolderMetaFor(targetVaultId, folder)
              } catch {}
            }
          } catch (err) {
            console.warn('[vaultStore] Failed to create entity note:', type, id, err)
          }
        }
      } catch {}

      if (createdNotes.length) {
        // Insert created notes into the correct collections
        if (targetVaultId === this.activeVaultId) {
          this.notes = [...createdNotes, ...this.notes]
        }
        if (this.allVaultsNotesLoaded) {
          const withVault = createdNotes.map((n) => ({
            ...n,
            vaultId: targetVaultId,
            vaultName: this.getVaultName(targetVaultId)
          }))
          this.allVaultsNotes = [...withVault, ...this.allVaultsNotes]
        }
        this._buildIndexes()
      }
      return createdNotes.length > 0
    },

    /**
     * Delete a note
     * @param {string} noteId - The note ID
     * @param {string} [vaultId] - Optional vault ID (required in all-vaults mode)
     */
    async deleteNote(noteId, vaultId = null) {
      // Determine which vault to delete from
      const targetVaultId = vaultId || this.activeVaultId
      if (!targetVaultId) throw new Error('No vault specified')

      await NoteService.deleteNote(targetVaultId, noteId)

      // Remove from current vault's notes
      this.notes = this.notes.filter((n) => n.id !== noteId)

      // Also remove from allVaultsNotes if in all-vaults mode
      if (this.allVaultsMode || this.allVaultsNotesLoaded) {
        this.allVaultsNotes = this.allVaultsNotes.filter((n) => n.id !== noteId)
      }

      this._buildIndexes()

      // Increment version to trigger graph rebuild and sync all components
      this.notesVersion++

      if (this.activeNoteId === noteId) {
        this.activeNoteId = null
      }
    },

    /**
     * Set active note
     */
    setActiveNote(noteId) {
      this.activeNoteId = noteId
    },

    /**
     * Set active note, switching to the correct vault if needed (All Vaults or cross-vault selection)
     * @param {string} noteId
     * @param {string|null} vaultId
     */
    async setActiveNoteSmart(noteId, vaultId = null) {
      if (!noteId) return
      // Resolve the note's vault when possible (even if caller didn't provide it).
      let resolvedVaultId = vaultId || null
      const cached = this.notesById && this.notesById[noteId] ? this.notesById[noteId] : null
      if (!resolvedVaultId && cached?.vaultId) resolvedVaultId = cached.vaultId

      // If we know the target vault and it's not active, switch first (awaited for index rebuild).
      if (resolvedVaultId && resolvedVaultId !== this.activeVaultId) {
        await this.setActiveVault(resolvedVaultId)
      }

      // If note is now in cache, select it.
      if (this.notesById && this.notesById[noteId]) {
        const now = this.notesById[noteId]
        if (!resolvedVaultId || now?.vaultId === resolvedVaultId) {
          this.activeNoteId = noteId
          return
        }
      }

      // Otherwise resolve via all-vaults list and switch vault, then select after load.
      try {
        if (!this.allVaultsNotesLoaded) {
          await this.loadAllVaultsNotes()
        }
        const all = Array.isArray(this.allVaultsNotes) ? this.allVaultsNotes : []
        const found = all.find(
          (n) => n && n.id === noteId && (!resolvedVaultId || n.vaultId === resolvedVaultId)
        )
        const foundVaultId = found?.vaultId || null
        if (foundVaultId && foundVaultId !== this.activeVaultId) {
          await this.setActiveVault(foundVaultId)
        }
        if (this.notesById && this.notesById[noteId]) {
          this.activeNoteId = noteId
        }
      } catch {
        // noop
      }
    },

    /**
     * Open or create a daily note for a specific date
     * @param {string} dateStr - ISO date string (YYYY-MM-DD)
     * @returns {Object|null} The note object
     */
    async openOrCreateDailyNote(dateStr) {
      if (!this.activeVaultId) throw new Error('No active vault')

      const slug = String(dateStr || '').trim()
      if (!slug) return null

      // Helper to convert timestamp to milliseconds
      const toMillis = (val) => {
        try {
          if (!val) return 0
          if (typeof val === 'number') return val
          if (typeof val === 'string') return Date.parse(val) || 0
          if (typeof val.toMillis === 'function') return val.toMillis()
          if (val._seconds) return val._seconds * 1000
          return 0
        } catch {
          return 0
        }
      }

      // Helper to format date as ISO string
      const pad = (n) => String(n).padStart(2, '0')
      const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

      // Check if any note was created on that day
      const onDay = (n) => {
        const ts = n.createdAt || n.updatedAt || null
        if (!ts) return false
        const dd = typeof ts === 'string' ? new Date(ts) : ts.toDate ? ts.toDate() : new Date(ts)
        return toIso(dd) === slug
      }

      const dayNotes = (this.notes || []).filter(onDay)
      if (dayNotes.length) {
        // Pick the most recently updated note from that day
        const getMillis = (n) => toMillis(n.updatedAt || n.createdAt || 0)
        const picked = dayNotes.slice().sort((a, b) => getMillis(b) - getMillis(a))[0]
        if (picked && picked.id) {
          this.setActiveNote(picked.id)
          return picked
        }
      }

      // Try by slug or title match
      const existing = this.notesBySlug[slug] || this.notes.find((n) => n.title === slug)
      if (existing && existing.id) {
        this.setActiveNote(existing.id)
        return existing
      }

      // Create a new daily note
      const context = this.userContext || {}
      const fm = `---\ndate: ${slug}\n---\n\n`
      const draft = {
        title: slug,
        slug,
        content: fm,
        createdAt: new Date(`${slug}T00:00:00`),
        permissions: this._getDefaultNotePermission(context, this.activeVaultId),
        frontmatter: {
          permissions: this._getDefaultNotePermission(context, this.activeVaultId),
          date: slug
        }
      }

      const created = await this.createNote(draft)
      if (created && created.id) {
        this.setActiveNote(created.id)
      }
      return created
    },

    /**
     * Get note by ID
     */
    getNoteById(noteId) {
      return this.notesById[noteId] || null
    },

    /**
     * Get note by slug
     */
    getNoteBySlug(slug) {
      return this.notesBySlug[slug] || null
    },

    /**
     * Search notes locally (synchronous - for computed properties)
     */
    searchNotes(query, options = {}) {
      if (!query || !this.notes.length) return this.notes

      const q = query.toLowerCase().trim()
      const { includeContent = false } = options

      return this.notes.filter((note) => {
        // Search in title
        if (note.title && note.title.toLowerCase().includes(q)) return true

        // Search in folder
        if (note.folder && note.folder.toLowerCase().includes(q)) return true

        // Search in tags
        if (note.tags && note.tags.some((t) => t.toLowerCase().includes(q))) return true

        // Optionally search in content
        if (includeContent && note.content && note.content.toLowerCase().includes(q)) return true

        return false
      })
    },

    /**
     * Search notes via service (async - includes backend search)
     */
    async searchNotesAsync(query, options = {}) {
      if (!this.activeVaultId) return []
      return NoteService.searchNotes(this.activeVaultId, query, options)
    },

    // ========================================================================
    // FOLDER OPERATIONS
    // ========================================================================

    /**
     * Set folder icon
     */
    async setFolderIcon(folderPath, icon) {
      if (!this.activeVaultId) throw new Error('No active vault')

      await VaultService.setFolderIcon(this.activeVaultId, folderPath, icon)

      // Update local state
      if (this.activeVault) {
        const folderMeta = { ...this.activeVault.folderMeta }
        if (icon) {
          folderMeta[folderPath] = { ...folderMeta[folderPath], icon }
        } else {
          delete folderMeta[folderPath]
        }
        this.activeVault = { ...this.activeVault, folderMeta }
      }
    },

    /**
     * Move notes to a folder
     */
    async moveNotesToFolder(noteIds, targetFolder) {
      if (!this.activeVaultId) throw new Error('No active vault')

      await NoteService.moveNotes(this.activeVaultId, noteIds, targetFolder)

      // Update local state
      for (const noteId of noteIds) {
        const idx = this.notes.findIndex((n) => n.id === noteId)
        if (idx !== -1) {
          this.notes[idx] = { ...this.notes[idx], folder: targetFolder }
        }
      }
      this._buildIndexes()
    },

    /**
     * Ensure a folder entry exists in vault metadata so it syncs across devices
     */
    async ensureFolderMeta(folderPath) {
      if (!this.activeVaultId || !folderPath) return
      try {
        const meta = await VaultService.updateFolderMeta(this.activeVaultId, folderPath, {
          explicit: true
        })
        if (this.activeVault) {
          const folderMeta = { ...(this.activeVault.folderMeta || {}) }
          folderMeta[folderPath] = {
            ...(folderMeta[folderPath] || {}),
            ...(meta || { explicit: true })
          }
          this.activeVault = { ...this.activeVault, folderMeta }
        }
      } catch (e) {
        console.warn('[vaultStore] ensureFolderMeta failed', e)
      }
    },

    /**
     * Rename folder metadata only (does not move notes)
     */
    async renameFolderMeta(oldPath, newPath) {
      if (!this.activeVaultId || !oldPath || !newPath || oldPath === newPath) return
      try {
        await VaultService.renameFolderMeta(this.activeVaultId, oldPath, newPath)
        // Refresh activeVault folderMeta map locally
        if (this.activeVault?.folderMeta) {
          const updated = {}
          for (const [path, meta] of Object.entries(this.activeVault.folderMeta)) {
            if (path === oldPath) {
              updated[newPath] = meta
            } else if (path.startsWith(oldPath + '/')) {
              const np = newPath + path.slice(oldPath.length)
              updated[np] = meta
            } else {
              updated[path] = meta
            }
          }
          this.activeVault = { ...this.activeVault, folderMeta: updated }
        }
      } catch (e) {
        console.warn('[vaultStore] renameFolderMeta failed', e)
      }
    },

    /**
     * Delete folder metadata for a path (and its children)
     */
    async deleteFolderMeta(folderPath) {
      if (!this.activeVaultId || !folderPath) return
      await this.deleteFolderMetaFor(this.activeVaultId, folderPath)
    },

    /**
     * Ensure a folder entry exists in a specific vault's metadata (cross-vault)
     */
    async ensureFolderMetaFor(vaultId, folderPath) {
      if (!vaultId || !folderPath) return
      try {
        const meta = await VaultService.updateFolderMeta(vaultId, folderPath, { explicit: true })
        // If updating the active vault, patch it
        if (this.activeVaultId === vaultId && this.activeVault) {
          const folderMeta = { ...(this.activeVault.folderMeta || {}) }
          folderMeta[folderPath] = {
            ...(folderMeta[folderPath] || {}),
            ...(meta || { explicit: true })
          }
          this.activeVault = { ...this.activeVault, folderMeta }
        }
        // Also patch the vaults list entry so all-vaults tree sees it
        const vIdx = (this.vaults || []).findIndex((v) => v && v.id === vaultId)
        if (vIdx >= 0) {
          const v = this.vaults[vIdx]
          const folderMeta = { ...(v.folderMeta || {}) }
          folderMeta[folderPath] = {
            ...(folderMeta[folderPath] || {}),
            ...(meta || { explicit: true })
          }
          this.vaults.splice(vIdx, 1, { ...v, folderMeta })
        }
      } catch (e) {
        console.warn('[vaultStore] ensureFolderMetaFor failed', e)
      }
    },

    /**
     * Delete folder metadata for a specific vault and path
     */
    async deleteFolderMetaFor(vaultId, folderPath) {
      if (!vaultId || !folderPath) return
      try {
        await VaultService.deleteFolderMeta(vaultId, folderPath)

        if (this.activeVaultId === vaultId && this.activeVault?.folderMeta) {
          const updated = {}
          for (const [path, meta] of Object.entries(this.activeVault.folderMeta)) {
            if (path !== folderPath && !path.startsWith(folderPath + '/')) {
              updated[path] = meta
            }
          }
          this.activeVault = { ...this.activeVault, folderMeta: updated }
        }

        const vIdx = (this.vaults || []).findIndex((v) => v && v.id === vaultId)
        if (vIdx >= 0) {
          const v = this.vaults[vIdx]
          const updated = {}
          for (const [path, meta] of Object.entries(v.folderMeta || {})) {
            if (path !== folderPath && !path.startsWith(folderPath + '/')) {
              updated[path] = meta
            }
          }
          this.vaults.splice(vIdx, 1, { ...v, folderMeta: updated })
        }
      } catch (e) {
        console.warn('[vaultStore] deleteFolderMetaFor failed', e)
      }
    },

    /**
     * Rename a folder
     */
    async renameFolder(oldPath, newPath) {
      if (!this.activeVaultId) throw new Error('No active vault')

      await NoteService.renameFolder(this.activeVaultId, oldPath, newPath)
      await VaultService.renameFolderMeta(this.activeVaultId, oldPath, newPath)

      // Reload notes to get updated folder paths
      await this.loadNotes()

      // Reload vault to get updated folder meta
      this.activeVault = await VaultService.getVault(this.activeVaultId)
    },

    // ========================================================================
    // SETTINGS
    // ========================================================================

    /**
     * Update vault settings
     */
    async updateSettings(settings) {
      if (!this.activeVaultId) throw new Error('No active vault')

      await VaultService.updateSettings(this.activeVaultId, settings)

      if (this.activeVault) {
        this.activeVault = {
          ...this.activeVault,
          settings: { ...this.activeVault.settings, ...settings }
        }
      }
    },

    /**
     * Bulk update note permissions in the active vault
     * @param {Object} options
     * @param {'PRIVATE'|'PUBLIC'|'CAMPAIGN'} options.target - target permission
     * @param {boolean} [options.onlyPrivate=true] - if true, only change currently PRIVATE notes
     * @returns {number} count of updated notes
     */
    async bulkSetNotePermissions(options = {}) {
      if (!this.activeVaultId) throw new Error('No active vault')
      const target = String(options.target || '').toUpperCase()
      const onlyPrivate = options.onlyPrivate !== false
      if (!['PRIVATE', 'PUBLIC', 'CAMPAIGN'].includes(target)) throw new Error('Invalid target')
      const list = Array.isArray(this.notes) ? this.notes.slice() : []
      let updated = 0
      for (const n of list) {
        const cur = String(n?.permissions || n?.frontmatter?.permissions || 'PRIVATE').toUpperCase()
        if (onlyPrivate && cur !== 'PRIVATE') continue
        if (cur === target) continue
        try {
          await this.updateNote(
            n.id,
            {
              permissions: target,
              frontmatter: { ...(n.frontmatter || {}), permissions: target }
            },
            n.vaultId || null,
            { preserveUpdatedAt: true }
          )
          updated++
        } catch {}
      }
      return updated
    },

    // ========================================================================
    // EDITING TRACKING
    // ========================================================================

    /**
     * Mark a note as being edited
     */
    markEditing(noteId) {
      if (!noteId) return
      this.editing[noteId] = true
    },

    /**
     * Clear editing flag for a note
     */
    clearEditing(noteId) {
      if (!noteId) return
      delete this.editing[noteId]
    },

    /**
     * Check if a note is being edited
     */
    isNoteEditing(noteId) {
      return !!this.editing[noteId]
    },

    /**
     * Optimistic local patch for UI reactivity (no persistence)
     */
    updateNoteLocal(noteId, patch = {}) {
      if (!noteId) return

      // Do not mutate slug in local patches to avoid link churn before save
      // eslint-disable-next-line no-unused-vars
      const { slug: _ignoreSlug, ...rest } = patch || {}

      // Update in current vault's notes
      const idx = this.notes.findIndex((n) => n && n.id === noteId)
      if (idx !== -1) {
        const current = this.notes[idx]
        const updated = { ...current, ...rest }
        this.notes[idx] = updated
      }

      // Also update in allVaultsNotes if loaded (for all-vaults mode)
      if (this.allVaultsNotesLoaded) {
        const allIdx = this.allVaultsNotes.findIndex((n) => n && n.id === noteId)
        if (allIdx !== -1) {
          const current = this.allVaultsNotes[allIdx]
          const updated = { ...current, ...rest }
          this.allVaultsNotes[allIdx] = updated
        }
      }

      this._buildIndexes()
    },

    /**
     * Record note access time
     */
    recordNoteAccess(noteId) {
      if (!noteId) return
      this.lastAccessed[noteId] = Date.now()
    },

    /**
     * Record link usage
     */
    recordLinkUsage(noteId) {
      if (!noteId) return
      this.usageCounts[noteId] = (this.usageCounts[noteId] || 0) + 1
      this.lastAccessed[noteId] = Date.now()
    },

    /**
     * Request the notes tree to jump to a vault/folder.
     * @param {Object} payload
     * @param {string} payload.vaultId
     * @param {string} payload.path
     */
    requestFolderJump({ vaultId, path } = {}) {
      if (!vaultId) return
      this.folderJump = {
        vaultId,
        path: String(path || ''),
        ts: Date.now()
      }
    },

    clearFolderJump() {
      this.folderJump = null
    },

    // ========================================================================
    // RESET
    // ========================================================================

    /**
     * Reset store state
     */
    reset() {
      this.vaults = []
      this.vaultsLoaded = false
      this.vaultsCacheInfo = null
      this.activeVaultId = null
      this.activeVault = null
      this.allVaultsNotes = []
      this.allVaultsNotesLoaded = false
      this.allVaultsNotesLoading = false
      this.notes = []
      this.notesLoaded = false
      this.notesCacheInfo = null
      this.activeNoteId = null
      this.notesById = {}
      this.notesBySlug = {}
      this.backlinks = {}
      this.mentions = {}
      this.editing = {}
      this.lastAccessed = {}
      this.usageCounts = {}
      this.folderJump = null
      this._userContext = null
      this._ensureDefaultVaultPromise = null
      this._loadVaultsPromise = null
      this._loadAllVaultsNotesPromise = null
      this.loading = false
      this.error = null
    }
  }
})

export default useVaultStore

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useVaultStore, import.meta.hot))
}
